import React from 'react';
import { getCoachCalendar } from '../../slices/coachesSlice';
import FullCalendar from '@fullcalendar/react';
import type { State } from '../../general/types';
import { useSelector } from 'react-redux';
import { Radio, Alert } from 'antd';
import timeGridPlugin from '@fullcalendar/timegrid';
import '../../schedule/coach-calendar/CoachCalendar.scss';
import EventPopover from './EventPopover';
import moment from 'moment';

const CoachCalendar = () => {
  const [fullSchedule, setFullSchedule] = React.useState([]);
  const [syncedDates, setSyncedDates] = React.useState([]);
  const [view, setView] = React.useState('day'); // 'day' | 'week'
  const [loaded, setLoaded] = React.useState(false);

  const currentUser = useSelector((state: State) => {
    return state.auth.currentUser;
  });

  const { dateFrom, dateTo } = React.useMemo(() => {
    if (view === 'week') {
      return {
        dateFrom: moment().startOf('isoWeek').format('YYYY-MM-DD'),
        dateTo: moment().endOf('isoWeek').format('YYYY-MM-DD'),
      };
    }
    const today = moment().format('YYYY-MM-DD');
    return { dateFrom: today, dateTo: today };
  }, [view]);

  const fetchCalendar = React.useCallback(async () => {
    const response = await getCoachCalendar(currentUser.id, dateFrom, dateTo);
    setFullSchedule(response.events);
    setSyncedDates(response.syncedDates);
    setLoaded(true);
  }, [currentUser.id, dateFrom, dateTo]);

  React.useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  function renderEventContent(eventInfo) {
    return <EventPopover eventInfo={eventInfo} onRefresh={fetchCalendar} />;
  }

  const dayCellClassNames = (arg) => {
    const cellDate = moment(arg.date).format('YYYY-MM-DD');
    return syncedDates.includes(cellDate)
      ? []
      : ['coach-calendar__day--unsynced'];
  };

  const dayHeaderClassNames = (arg) => {
    const cellDate = moment(arg.date).format('YYYY-MM-DD');
    return syncedDates.includes(cellDate)
      ? []
      : ['coach-calendar__day-header--unsynced'];
  };

  const today = moment().format('YYYY-MM-DD');
  // In daily view, show a banner if today hasn't been synced by admin yet.
  // Weekly view already greys unsynced day columns individually with a "Not
  // synced" label, so the banner is daily-only.
  const showUnsyncedBanner =
    loaded && view === 'day' && !syncedDates.includes(today);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '8px 16px',
        }}
      >
        <Radio.Group
          value={view}
          onChange={(e) => setView(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="day">Daily</Radio.Button>
          <Radio.Button value="week">Weekly</Radio.Button>
        </Radio.Group>
      </div>
      {showUnsyncedBanner && (
        <Alert
          message="Today's schedule has not been prepared yet"
          description="Your admin has not synced today's schedule. Please check back once it has been prepared."
          type="info"
          showIcon
          style={{ margin: '0 16px 8px 16px' }}
        />
      )}
      <FullCalendar
        key={view}
        plugins={[timeGridPlugin]}
        events={fullSchedule}
        initialView={view === 'week' ? 'timeGridWeek' : 'timeGridDay'}
        headerToolbar={{ left: '', center: 'title', right: '' }}
        slotEventOverlap={false}
        nowIndicator={true}
        eventContent={renderEventContent}
        dayCellClassNames={dayCellClassNames}
        dayHeaderClassNames={dayHeaderClassNames}
        height={'100vh'}
        slotMinTime={'04:00:00'}
        allDaySlot={false}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: true,
        }}
      />
    </div>
  );
};

export default CoachCalendar;
